import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, functions } from './firebase';
import TranslatableField from './TranslatableField';

const resizeImage = (file, maxWidth = 800, maxHeight = 800) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.85);
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

const AnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [shouldDeleteImage, setShouldDeleteImage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [userCounts, setUserCounts] = useState({});

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'Announcements'));
      const announcementsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a, b) => (b.validityStart?.toDate() || 0) - (a.validityStart?.toDate() || 0));
      setAnnouncements(announcementsData);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const getDatetimeString = (timestamp) => {
    if (!timestamp) return '';
    try {
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const offset = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - offset).toISOString().slice(0, 16);
    } catch (e) {
        return '';
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement({
      ...announcement,
      validityStart: getDatetimeString(announcement.validityStart),
      validityEnd: getDatetimeString(announcement.validityEnd),
    });
    setIsCreating(false);
    setImagePreview(announcement.imageURL || null);
    setImageFile(null);
    setShouldDeleteImage(false);
  };

  const handleCreate = () => {
    setEditingAnnouncement({
      title: '',
      content: '',
      targetText: '',
      validityStart: '',
      validityEnd: '',
      targetURL: '',
      isActive: false,
      imageURL: null,
    });
    setIsCreating(true);
    setImagePreview(null);
    setImageFile(null);
    setShouldDeleteImage(false);
  };

  const handleCancel = () => {
    setEditingAnnouncement(null);
    setIsCreating(false);
    setImageFile(null);
    setImagePreview(null);
    setShouldDeleteImage(false);
  };

  const handleToggleActive = async (announcement) => {
    setLoading(true);
    try {
      const docRef = doc(db, 'Announcements', announcement.id);
      await updateDoc(docRef, {
        isActive: !announcement.isActive
      });
      await fetchAnnouncements();
    } catch (error) {
      console.error("Error toggling active status:", error);
      alert("Failed to update status.");
      setLoading(false);
    }
  };

  const handleGetCount = async (announcement) => {
    setLoading(true);
    try {
      const getCount = httpsCallable(functions, 'getAnnouncementUserCount');
      const result = await getCount({ announcementId: announcement.id });
      setUserCounts(prev => ({
        ...prev,
        [announcement.id]: result.data
      }));
    } catch (error) {
      console.error("Error getting count:", error);
      alert("Failed to get user count.");
    }
    setLoading(false);
  };

  const handleProcessEmail = async (announcement) => {
    if (!window.confirm("Are you sure you want to process emails for this announcement?")) return;
    setLoading(true);
    try {
      const docRef = doc(db, 'Announcements', announcement.id);
      await updateDoc(docRef, {
        emailStatus: 'pending'
      });
      await fetchAnnouncements();
    } catch (error) {
      console.error("Error processing email:", error);
      alert("Failed to update email status.");
      setLoading(false);
    }
  };

  const handleDuplicate = async (announcement) => {
    if (!window.confirm("Duplicate this announcement?")) return;
    setLoading(true);
    try {
      const newDocRef = doc(collection(db, 'Announcements'));
      const newId = newDocRef.id;
      let newImageUrl = null;

      if (announcement.imageURL) {
        try {
          const response = await fetch(announcement.imageURL);
          if (response.ok) {
            const blob = await response.blob();
            const newImageRef = ref(storage, `Announcements/${newId}/image.jpg`);
            await uploadBytes(newImageRef, blob);
            newImageUrl = await getDownloadURL(newImageRef);
          }
        } catch (imgError) {
          console.warn("Error copying image for duplicate (skipping image):", imgError);
        }
      }

      const { id, ...dataToCopy } = announcement;
      const newData = { ...dataToCopy, id: newId, imageURL: newImageUrl, isActive: false }; // Default copy to inactive

      // Sanitize optional fields
      if (!newData.targetURL) newData.targetURL = null;
      if (!newData.targetText) newData.targetText = null;
      if (!newData.imageURL) newData.imageURL = null;

      await setDoc(newDocRef, newData);
      await fetchAnnouncements();
    } catch (error) {
      console.error("Error duplicating announcement:", error);
      alert("Failed to duplicate.");
    }
    setLoading(false);
  };

  const handleDelete = async (id, imageUrl) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      setLoading(true);
      try {
        // Delete image from Storage first
        if (imageUrl) {
            const imageRef = ref(storage, imageUrl);
            await deleteObject(imageRef).catch(err => {
                console.warn(`Could not delete image for announcement ${id}, it might not exist:`, err);
            });
        }

        await deleteDoc(doc(db, 'Announcements', id));
        await fetchAnnouncements();
      } catch (error) {
        console.error("Error deleting announcement:", error);
        alert("Failed to delete announcement.");
      }
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setEditingAnnouncement(prev => {
        const updated = { ...prev, [name]: newValue };
        // Clear targetText if targetURL is cleared
        if (name === 'targetURL' && (!newValue || newValue.trim() === '')) updated.targetText = '';
        return updated;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
        setShouldDeleteImage(false);
    }
  };

  const handleRemoveImage = () => {
      setImageFile(null);
      setImagePreview(null);
      setShouldDeleteImage(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploading(true);
    
    try {
      let docRef;
      if (isCreating) {
        // Just get a ref with a new ID, don't write to the server yet.
        docRef = doc(collection(db, 'Announcements'));
      } else {
        docRef = doc(db, 'Announcements', editingAnnouncement.id);
      }
      const docId = docRef.id;

      let imageUrlToSave = editingAnnouncement.imageURL || null;

      if (imageFile) {
          const imagePath = `Announcements/${docId}/image.jpg`;
          const imageRef = ref(storage, imagePath);
          
          // Cleanup: If replacing an image, ensure we delete the old one if it was at a different path (e.g. from a failed renew)
          if (editingAnnouncement.imageURL) {
             const oldImageRef = ref(storage, editingAnnouncement.imageURL);
             if (oldImageRef.fullPath !== imageRef.fullPath) {
                 await deleteObject(oldImageRef).catch(err => console.warn("Could not delete old image on replace:", err));
             }
          }

          const resizedImage = await resizeImage(imageFile, 800, 800);
          await uploadBytes(imageRef, resizedImage);
          imageUrlToSave = await getDownloadURL(imageRef);
      } else if (shouldDeleteImage && imageUrlToSave) {
          const oldImageRef = ref(storage, imageUrlToSave);
          await deleteObject(oldImageRef).catch(err => console.warn("Old image not found, could not delete:", err));
          imageUrlToSave = null;
      }

      // Exclude the old 'id' from editingAnnouncement and add the definitive one.
      const { id: formId, ...dataFromForm } = editingAnnouncement;
      const finalData = {
        ...dataFromForm,
        id: docId, // This ensures the ID is always correct and present.
        validityStart: Timestamp.fromDate(new Date(editingAnnouncement.validityStart)),
        validityEnd: Timestamp.fromDate(new Date(editingAnnouncement.validityEnd)),
        imageURL: imageUrlToSave,
      };

      // Sanitize optional fields
      if (!finalData.targetURL) finalData.targetURL = null;
      if (!finalData.targetText) finalData.targetText = null;
      if (!finalData.imageURL) finalData.imageURL = null;

      // Use setDoc. For creation, it creates. For editing, it merges.
      await setDoc(docRef, finalData, { merge: !isCreating });

      await fetchAnnouncements();
      handleCancel();
    } catch (error) {
      console.error("Error saving announcement:", error);
      alert("Failed to save announcement.");
    }
    setUploading(false);
    setLoading(false);
  };

  if (editingAnnouncement) {
    return (
      <div className="official-events-detail">
        <button className="back-button" onClick={handleCancel} style={{marginBottom: '20px', padding: '8px 16px', cursor: 'pointer'}}>
          &larr; Back to Announcements
        </button>
        <h2>{isCreating ? 'Create New Announcement' : 'Edit Announcement'}</h2>
        <form onSubmit={handleFormSubmit} style={{display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '600px'}}>
          {!isCreating && (
              <div className="form-group">
                  <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>ID:</label>
                  <input value={editingAnnouncement.id} readOnly style={{width: '100%', padding: '8px', backgroundColor: '#eee'}} />
              </div>
          )}
          <TranslatableField 
            label="Title" 
            name="title" 
            value={editingAnnouncement.title} 
            onChange={handleFormChange} 
          />
          <TranslatableField 
            label="Content" 
            name="content" 
            value={editingAnnouncement.content} 
            onChange={handleFormChange} 
            isTextarea={true} 
          />
          <div className="form-group">
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Validity Start:</label>
            <input type="datetime-local" name="validityStart" value={editingAnnouncement.validityStart} onChange={handleFormChange} required style={{width: '100%', padding: '8px'}} />
          </div>
          <div className="form-group">
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Validity End:</label>
            <input type="datetime-local" name="validityEnd" value={editingAnnouncement.validityEnd} onChange={handleFormChange} required style={{width: '100%', padding: '8px'}} />
          </div>
          <div className="form-group">
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Target URL (optional):</label>
            <input name="targetURL" value={editingAnnouncement.targetURL || ''} onChange={handleFormChange} style={{width: '100%', padding: '8px'}} />
          </div>
          <TranslatableField 
            label="Target Button Text" 
            name="targetText" 
            value={editingAnnouncement.targetText} 
            onChange={handleFormChange} 
            required={false}
            disabled={!editingAnnouncement.targetURL || editingAnnouncement.targetURL.trim() === ''}
          />
          <div className="form-group">
            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Image:</label>
            {imagePreview && (
                <div style={{marginBottom: '10px'}}>
                    <img src={imagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px', display: 'block', borderRadius: '4px' }} />
                    <button type="button" onClick={handleRemoveImage} style={{marginTop: '5px', fontSize: '0.8rem'}}>Remove Image</button>
                </div>
            )}
            <input type="file" accept="image/png, image/jpeg" onChange={handleFileChange} />
          </div>
          {!isCreating && (
            <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bold'}}>
                <input type="checkbox" name="isActive" checked={editingAnnouncement.isActive} onChange={handleFormChange} />
                Is Active
                </label>
            </div>
          )}
          <div style={{display: 'flex', gap: '10px'}}>
            <button type="submit" disabled={loading || uploading} style={{padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>
              {uploading ? 'Uploading...' : (loading ? 'Saving...' : 'Save')}
            </button>
            <button type="button" onClick={handleCancel} disabled={loading} style={{padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="official-events-list">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Announcements</h2>
        <button onClick={handleCreate} style={{padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>
          + New Announcement
        </button>
      </div>
      {loading ? <p>Loading...</p> : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {announcements.map(ann => (
            <li key={ann.id} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '15px', marginBottom: '15px', backgroundColor: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: 1 }}>
                    {ann.imageURL && (
                        <img src={ann.imageURL} alt={ann.title} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px' }} />
                    )}
                    <div>
                      <h3 style={{ margin: '0 0 10px 0' }}>
                        {typeof ann.title === 'string' ? ann.title : (ann.title?.en || 'Untitled')}
                        {typeof ann.title === 'object' && <small style={{marginLeft: '10px', color: '#999', fontSize: '0.75rem'}}>(Multilingual)</small>}
                      </h3>
                      <p style={{ margin: '0 0 10px 0', color: '#555', fontSize: '0.9rem' }}>
                        {typeof ann.content === 'string' ? ann.content : (ann.content?.en || 'No content')}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <small style={{ color: ann.isActive ? 'green' : 'red', fontWeight: 'bold' }}>
                          {ann.isActive ? '● Active' : '● Inactive'}
                        </small>
                        <button onClick={() => handleToggleActive(ann)} disabled={loading} style={{padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer'}}>
                            {ann.isActive ? 'Make Inactive' : 'Make Active'}
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                        <small style={{ color: '#555', fontWeight: 'bold' }}>
                          Email: {ann.emailStatus || 'draft'}
                        </small>
                        {(!ann.emailStatus || (typeof ann.emailStatus === 'string' && ann.emailStatus.toLowerCase().startsWith('draft'))) ? (
                            <>
                                <button onClick={() => handleGetCount(ann)} disabled={loading} style={{padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '3px'}}>
                                    Get Count
                                </button>
                                <button onClick={() => handleProcessEmail(ann)} disabled={loading} style={{padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer', backgroundColor: '#fd7e14', color: 'white', border: 'none', borderRadius: '3px'}}>
                                    Process
                                </button>
                            </>
                        ) : (
                            <button onClick={fetchAnnouncements} disabled={loading} style={{padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '3px'}}>
                                Refresh
                            </button>
                        )}
                      </div>
                      {userCounts[ann.id] && (
                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '2px' }}>
                          {userCounts[ann.id].totalUsers} total users, {userCounts[ann.id].unsubscribedCount} unsubscribed, {userCounts[ann.id].recipientsCount} recipients
                        </div>
                      )}
                      {ann.processingDetails && (
                        <div style={{ fontSize: '0.75rem', color: '#d9534f', marginTop: '5px', backgroundColor: '#f9f2f4', padding: '5px', borderRadius: '4px', border: '1px solid #ebccd1' }}>
                          <strong>Processing Details:</strong> {ann.processingDetails}
                        </div>
                      )}
                      <p style={{ margin: '10px 0 0 0', fontSize: '0.8rem', color: '#777' }}>
                        Valid: {ann.validityStart?.toDate().toLocaleString()} to {ann.validityEnd?.toDate().toLocaleString()}
                      </p>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button onClick={() => handleDuplicate(ann)} style={{padding: '5px 10px', backgroundColor: '#17a2b8', color: 'white'}}>Duplicate</button>
                  <button onClick={() => handleEdit(ann)} style={{padding: '5px 10px'}}>Edit</button>
                  <button onClick={() => handleDelete(ann.id, ann.imageURL)} style={{padding: '5px 10px', backgroundColor: '#dc3545', color: 'white'}}>Delete</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AnnouncementsPage;